/**
 * @fileoverview O pedido de estorno (31/10/2026, etapa 10 dos planos, parte 3),
 * em Faturamento: a cobrança escolhida na lista de faturas, o valor e o motivo.
 * Quem decide e devolve é a Sttelar (o super); aqui a pessoa só pede e
 * acompanha. Usa a moldura do modal da contratação.
 */

import React, { useEffect, useRef, useState } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { X } from "lucide-react";
import { toast } from "react-toastify";

import { billingErrorText } from "../PlanControls";
import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";
import { money } from "../../../utils/billingPlans";
import { formatDate } from "../../../utils/formatLocaleDate";
import usePlansStyles from "../PlansDialog/styles";

const t = (key, options) => i18n.t(`refundDialog.${key}`, options);

const MIN_REASON = 10;

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const text = dark ? "#FAFAFA" : "#09090B";
  return {
    body: { padding: "18px 22px 6px", display: "flex", flexDirection: "column", gap: 14 },
    summary: { margin: 0, fontSize: 14, lineHeight: 1.55, color: text },
    label: { fontSize: 13, fontWeight: 650, color: text, marginBottom: 6, display: "block" },
    reason: { minHeight: 96, padding: "10px 12px", resize: "vertical", lineHeight: 1.5 },
    hint: { margin: "6px 0 0", fontSize: 12.5, lineHeight: 1.5, color: muted },
  };
});

const RefundDialog = ({ invoice, onClose, onSent }) => {
  const plans = usePlansStyles();
  const classes = useStyles();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const field = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => field.current && field.current.focus(), 120);
    return () => clearTimeout(timer);
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/billing/refunds", { chargeId: invoice.id, reason: reason.trim() });
      toast.success(t("sent"));
      onSent();
    } catch (err) {
      toast.error(billingErrorText(err?.response?.data?.error));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth={false} classes={{ paper: plans.dialogPaper }} PaperProps={{ style: { width: "min(520px, calc(100vw - 32px))" } }} aria-labelledby="refund-dialog-title">
      <div className={plans.dialogHead}>
        <div>
          <h2 className={plans.dialogTitle} id="refund-dialog-title">
            {t("title")}
          </h2>
          <p className={plans.dialogText}>{t("subtitle")}</p>
        </div>
        <IconButton size="small" className={plans.dialogClose} onClick={onClose} disabled={busy} aria-label={t("close")}>
          <X size={18} />
        </IconButton>
      </div>
      <div className={classes.body}>
        <p className={classes.summary}>
          {t("summary", { refundable: money(invoice.refundableCents || invoice.paidAmountCents || invoice.amountCents), amount: money(invoice.paidAmountCents ?? invoice.amountCents), date: formatDate(invoice.createdAt) })}
        </p>
        <div>
          <label className={classes.label} htmlFor="refund-reason">
            {t("reason")}
          </label>
          <textarea
            id="refund-reason"
            ref={field}
            className={`${plans.input} ${classes.reason}`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={1000}
            placeholder={t("reasonPlaceholder")}
          />
          <p className={classes.hint}>{t("hint")}</p>
        </div>
      </div>
      <div className={plans.dialogFoot}>
        <button type="button" className={plans.secondary} onClick={onClose} disabled={busy}>
          {t("cancel")}
        </button>
        <button type="button" className={plans.primary} style={{ padding: "0 18px" }} onClick={send} disabled={busy || reason.trim().length < MIN_REASON}>
          {busy && <CircularProgress size={16} color="inherit" />}
          {t("send")}
        </button>
      </div>
    </Dialog>
  );
};

export default RefundDialog;
